import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#lockQuantityDisplay);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#lockQuantityDisplay);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Keep the qty input on the user's latest value until the cart request settles.
   * @param {Event} event
   */
  #lockQuantityDisplay = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof HTMLElement) || !this.contains(event.target)) return;

    const input = event.target.querySelector('input[ref="quantityInput"]');
    if (!(input instanceof HTMLInputElement)) return;

    const quantity = event.detail.quantity;
    input.setAttribute('data-pending-quantity', String(quantity));
    input.value = String(quantity);
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {Event} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line, cartLineKey: key } = event.detail;

    // Cart items require a line number or stable line item key
    if (!line && !key) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line ?? 0, key);
    }

    this.updateQuantity({
      line,
      key,
      quantity,
      action: 'change',
    });

    const lineItemRow = key
      ? this.#findCartItemRowByKey(key)
      : line
        ? this.refs.cartItemRows[line - 1]
        : null;

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * Called from `on:click="/onLineItemRemove/{{ line }}"` as `(line, event)`,
   * or from quantity change as `(line, key)`.
   * @param {number|string} line - The 1-based line item index.
   * @param {string|Event} [keyOrEvent] - Cart line item key, or the click event from on:click.
   */
  onLineItemRemove(line, keyOrEvent) {
    const lineNumber = Number(line);
    const key =
      typeof keyOrEvent === 'string' && keyOrEvent
        ? keyOrEvent
        : this.#getLineItemKey(lineNumber, keyOrEvent instanceof Event ? keyOrEvent : undefined);

    this.updateQuantity({
      line: lineNumber,
      key,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = key
      ? this.#findCartItemRowByKey(key)
      : this.refs.cartItemRows?.[lineNumber - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length === this.refs.cartItemRows.length;

    const templateId = this.isDrawer ? 'empty-cart-drawer-template' : 'empty-cart-template';
    const template = document.getElementById(templateId);
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * @param {string} key
   * @returns {HTMLElement | null}
   */
  #findCartItemRowByKey(key) {
    const rows = this.refs.cartItemRows;
    if (Array.isArray(rows)) {
      const match = rows.find((row) => row.dataset.key === key);
      if (match) return match;
    }
    return this.querySelector(`[data-key="${CSS.escape(key)}"]`);
  }

  /**
   * Resolve the stable cart line item key for a remove/update.
   * @param {number} line
   * @param {Event} [event]
   * @returns {string | undefined}
   */
  #getLineItemKey(line, event) {
    const fromTarget = event?.target instanceof Element ? event.target.closest('[data-key]') : null;
    const fromEvent = fromTarget instanceof HTMLElement ? fromTarget.dataset.key : undefined;
    if (fromEvent) return fromEvent;

    const row = this.refs.cartItemRows?.[line - 1];
    if (row instanceof HTMLElement && row.dataset.key) return row.dataset.key;

    const input = this.querySelector(`input[data-cart-line="${line}"]`);
    return input instanceof HTMLInputElement ? input.dataset.cartLineKey : undefined;
  }

  /**
   * Resolve sibling line keys for a cart row (discount-split paid lines).
   * Never include free-gift lines — a race used to apply the paid qty onto the gift.
   * @param {string} [key]
   * @param {number} [line]
   * @returns {string[]}
   */
  #getSiblingKeys(key, line) {
    const row = key
      ? this.#findCartItemRowByKey(key)
      : line
        ? this.refs.cartItemRows?.[line - 1]
        : null;

    if (row instanceof HTMLElement && row.dataset.boosterFreeGift === 'true') {
      return typeof key === 'string' && key ? [key] : row.dataset.key ? [row.dataset.key] : [];
    }

    const fromRow =
      row instanceof HTMLElement && row.dataset.siblingKeys
        ? row.dataset.siblingKeys.split(',').map((k) => k.trim()).filter(Boolean)
        : [];

    const primary =
      (typeof key === 'string' && key) ||
      (row instanceof HTMLElement ? row.dataset.key : '') ||
      fromRow[0];

    if (!primary) return [];

    const rest = fromRow.filter((k) => k !== primary);
    return [primary, ...rest];
  }

  /**
   * Prefer the paid line key captured at click time. If the DOM row is now a free gift
   * (morph race), fall back to the paid row for the same variant.
   * @param {string} [key]
   * @param {number} [line]
   * @returns {{ key?: string, line?: number }}
   */
  #resolvePaidUpdateTarget(key, line) {
    const row = key
      ? this.#findCartItemRowByKey(key)
      : line
        ? this.refs.cartItemRows?.[line - 1]
        : null;

    if (!(row instanceof HTMLElement) || row.dataset.boosterFreeGift !== 'true') {
      return { key, line };
    }

    const variantId = row.dataset.variantId;
    if (!variantId) return { key, line };

    const paidRow = Array.from(this.querySelectorAll('tr.cart-items__table-row[data-variant-id]')).find(
      (el) =>
        el instanceof HTMLElement &&
        el.dataset.variantId === variantId &&
        el.dataset.boosterFreeGift !== 'true'
    );

    if (paidRow instanceof HTMLElement && paidRow.dataset.key) {
      return { key: paidRow.dataset.key, line };
    }

    return { key, line };
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} [config.line] - The line.
   * @param {string} [config.key] - The cart line item key (preferred; stable across concurrent cart changes).
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const resolved = this.#resolvePaidUpdateTarget(config.key, config.line);
    const line = resolved.line;
    const key = resolved.key;
    const { quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const siblingKeys = this.#getSiblingKeys(key, line);
    /** @type {Record<string, unknown>} */
    const payload = {
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    // Discount engine can keep the same paid variant on multiple lines. Collapse siblings
    // into one key whenever quantity changes so the drawer does not keep showing splits.
    if (siblingKeys.length > 1) {
      const keepKey = siblingKeys[0];
      if (!keepKey) {
        console.error('Cart update missing sibling key');
        this.#enableCartItems();
        return;
      }
      /** @type {Record<string, number>} */
      const updates = { [keepKey]: quantity };
      for (let i = 1; i < siblingKeys.length; i++) {
        const siblingKey = siblingKeys[i];
        if (siblingKey) updates[siblingKey] = 0;
      }
      payload.updates = updates;
    } else if (typeof key === 'string' && key) {
      payload.id = key;
      payload.quantity = quantity;
    } else if (line) {
      payload.line = line;
      payload.quantity = quantity;
    } else {
      console.error('Cart update missing line and key');
      this.#enableCartItems();
      return;
    }

    const body = JSON.stringify(payload);
    const endpoint = payload.updates
      ? Theme.routes.cart_update_url || '/cart/update.js'
      : Theme.routes.cart_change_url;

    cartTotal?.shimmer();

    fetch(`${endpoint}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(parsedResponseText, line, key);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#clearPendingQuantities();
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {Event} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (parsedResponseText, line, key) => {
    const quantitySelector = key
      ? this.querySelector(`input[data-cart-line-key="${CSS.escape(key)}"]`)?.closest(
          'cart-quantity-selector-component, quantity-selector-component'
        )
      : line
        ? this.refs.quantitySelectors[line - 1]
        : null;
    const quantityInput =
      quantitySelector instanceof Element
        ? quantitySelector.querySelector('input') || quantitySelector
        : null;

    if (!(quantityInput instanceof HTMLInputElement)) throw new Error('Quantity input not found');

    quantityInput.removeAttribute('data-pending-quantity');
    quantityInput.value = quantityInput.defaultValue;

    const errorLine = line || Number(quantityInput.dataset.cartLine) || 1;
    const cartItemError = this.refs[`cartItemError-${errorLine}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${errorLine}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    // Own quantity request owns the DOM until it finishes — avoid mid-update flashes.
    if (this.classList.contains('cart-items-disabled')) return;
    if (this.querySelector('input[data-pending-quantity]')) return;

    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (!(event instanceof CartUpdateEvent) && !(event instanceof CartAddEvent)) return;
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Drop optimistic qty locks after the server morph lands.
   */
  #clearPendingQuantities() {
    this.querySelectorAll('input[data-pending-quantity]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.removeAttribute('data-pending-quantity');
      }
    });
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
